import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PatientVital } from '@/types/vitals-api';

/**
 * GET /api/Patients/GetVitalsList?PatientId={}&PageNumber=1&PageSize={}
 *
 * Patient-wide vitals history — drives the per-cell sparkline trend
 * chart on the in-tab Vitals grid. Mirrors Flutter
 * `GetPatientVitalsHistoryEvent` → `GetVitalsList`
 * (`vitals_tab.dart:113-123`).
 *
 * Distinct from `useEncounterVitalsHistory` which is encounter-scoped
 * and powers the "View Vitals" drawer. This endpoint returns ALL
 * vitals across encounters for a patient, paged.
 *
 * Response envelope (per Flutter `PatientVitalsHistory`):
 *   { vitalHistoryRequest: PatientVital[] }
 *
 * AbortSignal forwarded per project rule.
 *
 * Page size matches Flutter's heuristic in `vitals_tab.dart:118-120`:
 *   - 36 by default (3 dozen historical points)
 *   - 32 for patients > 2y old that aren't female (smaller curve)
 * Caller passes the value; this hook doesn't decide.
 */

export interface PatientVitalsListResponse {
  vitalHistoryRequest: PatientVital[] | null;
}

export interface UsePatientVitalsListParams {
  patientId: string | null | undefined;
  pageSize?: number;
  enabled?: boolean;
}

export function usePatientVitalsList(
  params: UsePatientVitalsListParams,
): UseQueryResult<PatientVitalsListResponse> {
  const { patientId, pageSize = 36, enabled = true } = params;

  return useQuery<PatientVitalsListResponse>({
    queryKey: ['patient-vitals-list', patientId, pageSize],
    enabled: enabled && !!patientId,
    queryFn: ({ signal }) =>
      get<PatientVitalsListResponse>(
        `/api/Patients/GetVitalsList?PatientId=${encodeURIComponent(
          patientId as string,
        )}&PageNumber=1&PageSize=${pageSize}`,
        { signal },
      ),
  });
}
